<template>
	<section v-if="!isServerError" data-testid="booking-component">

		<div class="container">
			<div class="row">
				<div class="col-xs-12 p-t-3">
					<h1 class="h2" data-testid="bookingPageHeader">{{ heading }}</h1>
				</div>
			</div>
			<div class="row row-xs-bottom">
				<div class="col-xs-12 col-md-8">
					<p>{{ subHeading }}</p>
				</div>
				<div class="col-xs-12 col-md-4 text-md-right">
					<p :class="{ 'm-b-sm' : showAvailableSessions }"><b>Location:</b> {{ locationName }}</p>
					<p v-if="showAvailableSessions" data-testid="availableSessions">
						<button v-if="selectedServiceSessions > 0" class="btn btn-link btn-link-simple" @click="showSessionsAvailableDetails()"><b>{{ selectedServiceSessions }}</b> Session<span v-if="selectedServiceSessions !== 1">s</span> Available</button>
						<span v-else><b>0</b> Sessions Available</span>
					</p>
				</div>
			</div>

		</div>

		<!-- Filters -->
		<SearchFilters
			v-if="facetData"
			filterPage="booking"
			id="filtersSub"
			ref="filtersSub"
			:filterTypes="filterTypes"
			:facets="facetData"
			:queryFilters="queryFilters"
			prefix="ltf-booking-filter-"
			:savedSearchFilters="[]"
			@changed="handleFiltersChanged"
			itemCssClass="col-xs-12 col-md-6 col-xl-3"
		/>

		<!-- Chiclets -->
		<Chiclets
			:selected="searchChiclets"
			:allowFavorites="false"
			@removed="handleRemovedChiclet"
			@clearAllChiclets="handleClearAllChiclets"
		/>

		<!-- AVAILABLE TRAINERS / DETAILED SCHEDULE Nav Tabs -->
		<div class="container m-t-1" v-if="hasBioStep">
			<ul class="nav nav-tabs nav-tabs-alt nav-tabs-border">
				<li class="nav-item">
					<a :class="['nav-link', {'active': currentStep === 'bio'}]" data-testid="availableTrainersTab" role="button" data-anchor-button href="#" @click="handleLoadBio()" :aria-current="currentStep === 'bio' ? 'page' : null"><span class="hidden-xs-down">Available </span>Trainers</a>
				</li>
				<li class="nav-item">
					<a :class="['nav-link', {'active': currentStep === 'schedule'}]" data-testid="detailedScheduleTab" role="button" data-anchor-button href="#" @click="handleLoadSchedule()" :aria-current="currentStep === 'schedule' ? 'page' : null"><span class="hidden-xs-down">Detailed </span>Schedule</a>
				</li>
			</ul>
		</div>

		<transition name="fade" mode="out-in" appear>
			<!-- Bio Step -->
			<BioSub
				data-testid="bioSub"
				v-if="currentStep==='bio' && hasBioStep && instructorData && instructorData.length"
				:instructors="instructorData"
				:selectedServiceSessions="selectedServiceSessions"
				@scheduleSessionWith="handleScheduleSessionWith"
				@isWaiting="handleIsWaiting"
			/>

			<!-- Schedule Step -->
			<ScheduleSub
				data-testid="scheduleSub"
				v-else-if="currentStep==='schedule' && scheduleData && scheduleData.total !== 0"
				:day="scheduleData"
				:selectedServiceSessions="selectedServiceSessions"
				:selectedServiceActivityIds="searchFilters.service"
			/>

			<!-- No Results Message -->
			<div v-else-if="!isWaiting" class="container m-t-2">
				<div class="planner-entry no-results" data-testid="noResultsFoundTxt">No results found.<span v-if="selectedServiceSessions === 0"> Please purchase sessions in the club.</span></div>
			</div>
		</transition>

		<!-- Sessions Available Details Modal -->
		<Modal ref="sessionsAvailableDetailsModal" ariaLabelled="sessionsAvailableDetailsModalTitle" data-testid="sessionsAvailableModal">
			<template #default>
				<h2 class="h3" data-testid="sessionsAvailableModalTitle">Session Credits Available</h2>
				<table class="table-simple">
					<caption class="sr-only">Session Credits Available</caption>
					<thead>
						<tr>
							<th scope="col">Service</th>
							<th scope="col">Sessions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(sessionType, index) in availableSessions" :key="index">
							<td>{{ sessionType.name }}</td>
							<td>{{ sessionType.count }}</td>
						</tr>
					</tbody>
				</table>
				<p class="small">*Products on account with no registration will not be represented in list above.</p>
			</template>
			<template #footer>
				<button type="button" class="btn btn-primary" data-dismiss="modal" data-testid="closeBtn">Close</button>
			</template>
		</Modal>

		<!-- Loading Spinner -->
		<div role="alert" aria-live="assertive">
			<transition name="fade" appear>
				<div class="spinner-overlay" data-testid="spinner" v-show="isWaiting">
					<p class="sr-only">Loading…</p>
					<LoadingSpinner />
				</div>
			</transition>
		</div>

	</section>
</template>

<script>
import { defineComponent } from 'vue';
import _ from 'lodash';
import { baseComponentMixin } from '@/mixins/baseComponent.mixin';
import LoadingSpinner from '@/components/schreg-common/LoadingSpinner.vue';
import Modal from '@/components/schreg-common/Modal.vue';
import SearchFilters from '@/components/schreg-common/SearchFilters.vue';
import Chiclets from '@/components/schreg-common/Chiclets.vue';
import { UxSchedulesApi } from '@/apis/uxSchedules.api';
import { StateService } from '@/services/state.service';
import { FilteringService, FilteringServicePages } from '@/services/filtering.service';
import { FilterTypes } from '@/services/filtering.service.config';
import { FrameworkService } from '@/services/framework.service';
import { NotificationService } from '@/services/notification.service';

import BioSub from './sub/BioSub.vue';
import ScheduleSub from './sub/ScheduleSub.vue';

export default defineComponent({
	name: 'BookingComponent',
	config: {
		key: 'schreg.booking',
		devPath: 'pt-booking',
		exported: true
	},
	components: {
		LoadingSpinner,
		Modal,
		SearchFilters,
		Chiclets,
		BioSub,
		ScheduleSub
	},
	mixins: [
		baseComponentMixin
	],
	data() {
		return {
			query: {
				selectedDate: null
			},
			heading: 'Schedule Your Session',
			subHeading: '',
			locationName: '',
			availableSessions: null,
			selectedServiceSessions: null,
			clubPhone: null,
			hasBioStep: false,
			isPtOneOnOne: false,
			currentStep: 'bio',
			selectedDate: null,
			maxDay: null,
			filterTypes: [],
			searchChiclets: [],
			searchFilters: null,
			queryFilters: [],
			facetData: null,
			instructorData: null,
			scheduleData: null,
			instructorValidDates: [],
			firstDate: null
		};
	},
	computed: {
		showAvailableSessions() {
			return this.availableSessions !== null && this.selectedServiceSessions !== null;
		}
	},
	mounted() {
		this.getFrameworkData(this.completeMounting, ['ClubStore', 'ProfileStore', 'UrlStore']);
	},
	methods: {
		completeMounting() {
			this.heading = StateService.authoredSettings.header || 'Schedule Your Session';
			this.subHeading = StateService.authoredSettings.subheader || 'Select from the options below to set up your session.';
			this.locationName = StateService.getLocationName();

			this.filterTypes = [
				FilterTypes.service,
				FilterTypes.trainer,
				FilterTypes.date,
				FilterTypes.timeOfDay
			];

			this.queryFilters = FilteringService.getSelectedFiltersFromQuery(this.filterTypes);
			this.searchFilters = FilteringService.getInitialFilters(FilteringServicePages.booking, this.queryFilters, this.filterTypes);
			this.searchChiclets = this.searchFilters.chiclets;

			this.clubPhone = FrameworkService.data('club.phone');

			if (!this.userAuthenticated) {
				this.mountServerError(
					true,
					`Please <a href="${FrameworkService.globalUrl('loginResource')}">log in</a> to schedule your session.`
				);
				return;
			}

			this.getData().then(response => {

				if (!response) return;

				// Default to first Service
				if (this.searchFilters.service === null) {
					const serviceFilter = response.data.filters.find(f => f.filter === 'service');
					if (serviceFilter && serviceFilter.values && serviceFilter.values.length) {
						this.searchFilters.service = serviceFilter.values[0].value; // Note: This is the first in the data, not the sorted filter
						this.$refs.filtersSub.selectFilterValue(FilterTypes.service, this.searchFilters.service, false); // This will NOT trigger a new API request with the selected service (per Troy, if no services are passed, the API selects the first service the member has sessions for and returns results for that)

						// Display the number of sessions available for this service
						const selectedService = this.availableSessions.find(availableSession => availableSession.value === this.searchFilters.service);
						this.selectedServiceSessions = selectedService ? selectedService.count : null;
					}
				}

				this.hasBioStep = response.data.options && response.data.options.showTrainer; // TODO: This doesn't account for PT Onboarding Session with showTrainer query string parameter

				// Check for "trainer" from query string in (bios) results
				const trainerParam = StateService.getQuery('trainer');
				const trainerFound = trainerParam && _.find(this.instructorData, ['name', trainerParam]);

				if (trainerFound || !this.hasBioStep) {
					this.handleScheduleSessionWith(trainerParam);
				} else if (trainerParam && !trainerFound) {
					const phoneDisplay = Vue.filter('phoneFilter')(this.clubPhone, 'display');
					const phoneUrl = Vue.filter('phoneFilter')(this.clubPhone, 'telUrl');
					const phoneAria = Vue.filter('phoneFilter')(this.clubPhone, 'ariaLabel');
					const clubPhoneTelLink = `<a href="${phoneUrl}" :aria-label="${phoneAria}">${phoneDisplay}</a>`;
					NotificationService.info('<p>Trainer unavailable. Choose from available trainers below or call the club at ' + clubPhoneTelLink + '.</p>').trigger();
				}
			});
		},
		completeChange() {
			this.getData().then(() => {
				// Only enable "Any Date" option on the bio step
				if (this.currentStep === 'schedule') {
					this.$refs.filtersSub.selectFilterValue(FilterTypes.date, this.searchFilters.date, false);
				}
				this.$refs.filtersSub.enableDisableFilterIndex(FilterTypes.date, 0, (this.currentStep === 'bio'));

			});
		},
		getData() {
			return UxSchedulesApi
				.getBookingActivities(this.searchFilters)
				.then(response => {
					if (!response.ok) {
						if (response.status === 401) {
							return this.mountServerError(
								true,
								`Please <a href="${FrameworkService.globalUrl('loginResource')}">log in</a> to schedule your session.`
							);
						}
						return this.mountServerError(true, response.data.error);
					}
					// We only show a single day on the schedule, so choose the first and only (don't populate scheduleData if a date hasn't been picked or there are no results)
					this.scheduleData = response.data.results.length === 1 ? response.data.results[0] : null;
					this.facetData = response.data.filters;
					this.availableSessions = _.sortBy(response.data.availableSessions, 'name');
					this.instructorData = _.sortBy(response.data.bios, 'name');

					if (this.searchFilters.service) {
						const selectedService = this.availableSessions.find(availableSession => availableSession.value === this.searchFilters.service);
						this.selectedServiceSessions = selectedService ? selectedService.count : null;
					}

					if (!this.availableSessions || this.availableSessions.length === 0) {
						this.selectedServiceSessions = 0;
						NotificationService.info('<p>0 remaining sessions. Please purchase sessions in the club.</p>').trigger();
					}

					function findInstructorOnDate(dateItem, trainer) {
						for (let dayPartItem of dateItem.dayParts) {
							for (let startTimeItem of dayPartItem.startTimes) {
								for (let activityItem of startTimeItem.activities) {
									if (activityItem.instructors.find(i => i.name === trainer.name)) return true;
								}
							}
						}
						return false;
					}
					const startDateFilter = response.data.filters.find(f => f.filter === 'startdate');
					if (startDateFilter && startDateFilter.values && startDateFilter.values.length) {
						this.firstDate = startDateFilter.values[0].value;
					}
					const trainerDates = [];
					for (let trainer of response.data.bios) {
						const validDates = [];
						for (let dateItem of response.data.results) {
							if (findInstructorOnDate(dateItem, trainer)) {
								validDates.push(dateItem.date);
							}
						}
						trainerDates.push({
							name: trainer.name,
							dates: validDates
						});
					}
					this.instructorValidDates = trainerDates;

					this.isWaiting = false;
					return Promise.resolve(response);
				});
		},

		handleScheduleSessionWith(instructorName) {
			this.currentStep = 'schedule';
			this.$refs.filtersSub.selectFilterValue(FilterTypes.trainer, instructorName, true);
		},

		handleIsWaiting(bool) {
			this.isWaiting = bool;
		},

		handleRemovedChiclet(chiclet) {
			this.$refs.filtersSub.deselectFilterValue(chiclet.type, chiclet.value);
		},

		handleClearAllChiclets() {
			// This component doesn't add filters to the query string, so just reload
			window.location.reload();
		},

		handleFiltersChanged(updatedFilters) {
			this.isWaiting = true;
			this.searchFilters = FilteringService.getUpdatedFilters(FilteringServicePages.booking, updatedFilters, this.filterTypes);
			this.searchChiclets = this.searchFilters.chiclets;

			if (this.currentStep === 'schedule' && this.searchFilters.date === null) {
				if (this.searchFilters.organizers.length && this.searchFilters.organizers[0]) {
					const firstTrainer = this.searchFilters.organizers[0];
					this.searchFilters.date = this.instructorValidDates.find(i => i.name === firstTrainer).dates[0];
				} else {
					this.searchFilters.date = this.firstDate;
				}
			}

			this.completeChange();
		},

		handleLoadSchedule() {
			this.isWaiting = true;
			this.currentStep = 'schedule';
			if (this.searchFilters.date === null) {
				if (this.searchFilters.organizers.length) {
					const firstTrainer = this.searchFilters.organizers[0];
					this.searchFilters.date = this.instructorValidDates.find(i => i.name === firstTrainer).dates[0];
				} else {
					this.searchFilters.date = this.firstDate;
				}
			}
			this.completeChange();
		},

		handleLoadBio() {
			this.isWaiting = true;
			this.currentStep = 'bio';
			this.completeChange();
		},

		showSessionsAvailableDetails() {
			this.$refs.sessionsAvailableDetailsModal.show();
		}
	}
});
</script>

<style lang="scss" scoped>
@import "@/common/styles/loading.scss";
@import "@/common/styles/panels.scss";

.no-results {
	color: $gray-5;
	box-shadow: 0 0 0 1px $gray-11;
	text-align: center;
}

// Transitions
::v-deep .fade-items-enter-active,
::v-deep .fade-items-leave-active {
	transition-property: opacity;
	transition-duration: 300ms;
	transition-timing-function: linear;
}
::v-deep .fade-items-enter,
::v-deep .fade-items-leave-to {
	opacity: 0;
}
::v-deep .fade-items-move {
	transition: all 300ms ease-in-out;
}
::v-deep .fade-items-leave-active {
	left: 0; /* Maintain width */
	right: 0; /* Maintain width */
	position: absolute;
}
</style>
